import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { CourseService, Course, Certificate } from './course.service';
import { CourseService as InstructorCourseService, Course as InstructorCourse } from '../instructor/course.service';
import { AuthService } from '../auth/auth.service';
import { IconComponent } from '../shared/components/icon/icon.component';
import { CourseFilterBarComponent } from '../shared/components/course-filter-bar/course-filter-bar.component';
import { CourseCardComponent } from './components/course-card/course-card.component';
import { CourseListDataService } from './course-list-data.service';
import { SortOption } from '../shared/utils/course-sort-filter.utils';
import { PaymentsCheckoutService } from '../payments/payments-checkout.service';

@Component({
  selector: 'app-course-list',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent, CourseFilterBarComponent, CourseCardComponent],
  templateUrl: './course-list.component.html',
  styleUrls: ['./course-list.component.scss']
})
export class CourseListComponent implements OnInit, OnDestroy {
  courses: Course[] = [];
  certificates: Certificate[] = [];
  isLoading = true;
  isLoadingInstructorCourses = false;
  errorMessage: string | null = null;
  hasInstructorRole = false;
  selectedOrgFilter = 'all';
  activeTab: 'learning' | 'teaching' = 'learning';
  showArchivedTeachingCourses = false;

  // KAN-353: renew-access checkout state. Busy id blocks double-submits while
  // the hosted-checkout redirect is in flight (success navigates away).
  checkoutBusyCourseId: string | null = null;
  checkoutError: string | null = null;

  // Flattened instructor courses for grid display
  allInstructorCourses: InstructorCourse[] = [];

  learnerSortOptions: SortOption[] = [
    { value: 'title', label: 'Title' },
    { value: 'computedProgress', label: 'Progress' },
    { value: 'computedStatus', label: 'Status' },
    { value: 'assignedAt', label: 'Date Assigned' },
  ];

  instructorSortOptions: SortOption[] = [
    { value: 'title', label: 'Title' },
    { value: 'status', label: 'Status' },
    { value: 'createdAt', label: 'Date Created' },
    { value: 'updatedAt', label: 'Last Updated' },
  ];

  private orgChangeSubscription: Subscription | null = null;
  private instructorCoursesSubscription: Subscription | null = null;
  private instructorLoadTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private courseService: CourseService,
    private instructorCourseService: InstructorCourseService,
    private authService: AuthService,
    private router: Router,
    public data: CourseListDataService,
    private checkoutService: PaymentsCheckoutService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const currentUser = this.authService.getCurrentUser();
    this.hasInstructorRole = !!(currentUser && this.hasInstructorPermissions(currentUser));

    if (currentUser?.currentOrgId) {
      this.selectedOrgFilter = currentUser.currentOrgId;
    }

    this.orgChangeSubscription = this.authService.userOrganizations$.subscribe(() => {
      const user = this.authService.getCurrentUser();
      if (user?.currentOrgId) {
        this.hasInstructorRole = !!(user && this.hasInstructorPermissions(user));
        if (user.currentOrgId !== this.selectedOrgFilter) {
          this.selectedOrgFilter = user.currentOrgId;
        }
        this.loadCourses();
        if (this.hasInstructorRole) {
          this.loadInstructorCourses();
        } else {
          this.allInstructorCourses = [];
        }
        this.cdr.markForCheck();
      }
    });

    this.loadCourses();
    this.loadCertificates();
    if (this.hasInstructorRole) {
      this.loadInstructorCourses();
      // Default to teaching tab for instructors
      this.activeTab = 'teaching';
    }
  }

  ngOnDestroy(): void {
    this.orgChangeSubscription?.unsubscribe();
    this.instructorCoursesSubscription?.unsubscribe();
    if (this.instructorLoadTimer !== null) clearTimeout(this.instructorLoadTimer);
  }

  private hasInstructorPermissions(user: any): boolean {
    if (user?.isSystemAdmin) return true;
    if (user?.currentOrgRole && ['admin', 'instructor'].includes(user.currentOrgRole)) return true;
    return false;
  }

  // ===== Data Loading =====

  loadCourses(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.courseService.getUserCourses().subscribe({
      next: (courses: Course[]) => {
        this.courses = courses;
        this.data.extractOrganizations(courses);
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Failed to load your enrolled courses. Please try again later.';
        this.cdr.markForCheck();
      }
    });
  }

  loadInstructorCourses(): void {
    this.instructorCoursesSubscription?.unsubscribe();
    if (this.instructorLoadTimer !== null) clearTimeout(this.instructorLoadTimer);
    this.isLoadingInstructorCourses = true;
    this.instructorLoadTimer = setTimeout(() => {
      this.instructorLoadTimer = null;
      this.instructorCoursesSubscription = this.instructorCourseService.getAllInstructorCourses(this.selectedOrgFilter, {
        excludeArchived: this.showArchivedTeachingCourses ? undefined : true
      }).subscribe({
        next: (courses: InstructorCourse[]) => {
          this.allInstructorCourses = courses || [];
          this.isLoadingInstructorCourses = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.allInstructorCourses = [];
          this.isLoadingInstructorCourses = false;
          this.cdr.markForCheck();
        }
      });
    }, 500);
  }

  loadCertificates(): void {
    this.courseService.getUserCertificates().subscribe({
      next: (certificates) => { this.certificates = certificates; this.cdr.markForCheck(); },
      error: () => {}
    });
  }

  // ===== Computed filtered lists =====

  get filteredInstructorCourses(): InstructorCourse[] {
    return this.data.getFilteredSortedInstructorCourses('instructor-global', this.allInstructorCourses);
  }

  get filteredLearnerCourses(): Course[] {
    return this.data.getFilteredSortedCourses('learner-global', this.courses);
  }

  // ===== Status/Progress Methods =====

  // KAN-411 round 3: pass the learner's certificate presence so genuine
  // completion (gate passed -> cert issued) drives the status badge instead of
  // module progress alone.
  getCourseStatus(course: Course) { return this.data.getCourseStatus(course, !!this.getCertificateForCourse(course.id)); }
  getProgressPercentage(course: Course) { return this.data.getProgressPercentage(course); }
  getCertificateForCourse(courseId: string) { return this.data.getCertificateForCourse(courseId, this.certificates); }
  shouldShowContinueButton(course: Course) { return this.data.shouldShowContinueButton(course, !!this.getCertificateForCourse(course.id)); }
  getCurrentOrgName() { return this.data.getCurrentOrgName(this.selectedOrgFilter); }

  // ===== Search/Sort =====

  onShowArchivedChange(show: boolean): void {
    this.showArchivedTeachingCourses = show;
    this.loadInstructorCourses();
  }

  onInstructorSearchChange(term: string): void {
    this.data.setOrgSearch('instructor-global', term);
  }

  onInstructorSortChange(event: { field: string; order: 'asc' | 'desc' }): void {
    this.data.setOrgSort('instructor-global', event.field, event.order);
  }

  onLearnerSearchChange(term: string): void {
    this.data.setOrgSearch('learner-global', term);
  }

  onLearnerSortChange(event: { field: string; order: 'asc' | 'desc' }): void {
    this.data.setOrgSort('learner-global', event.field, event.order);
  }

  // ===== Navigation =====

  onInstructorCourseClick(courseId: string): void {
    this.router.navigate(['/instructor/courses', courseId, 'manage']);
  }

  onLearnerCourseClick(courseId: string): void {
    this.router.navigate(['/courses', courseId]);
  }

  onContinueCourse(courseId: string): void {
    const course = this.courses.find(c => c.id === courseId);
    if (!course) return;

    const cachedModuleId = this.data.lastAccessedModule.get(courseId);
    if (cachedModuleId) {
      this.router.navigate(['/courses', courseId], { queryParams: { module: cachedModuleId } });
      return;
    }

    this.courseService.getCourseProgress(courseId).subscribe({
      next: (progressRecords) => {
        const records = Array.isArray(progressRecords) ? progressRecords : [];
        this.data.findLastAccessedModule(courseId, records);
        const lastModuleId = this.data.lastAccessedModule.get(courseId);
        if (lastModuleId) {
          this.router.navigate(['/courses', courseId], { queryParams: { module: lastModuleId } });
        } else {
          this.router.navigate(['/courses', courseId]);
        }
      },
      error: () => {
        this.router.navigate(['/courses', courseId]);
      }
    });
  }

  // ===== Renew access (KAN-353) =====

  /** Starts a server-priced checkout for an access-expired paid course. */
  onRenewAccess(courseId: string): void {
    if (this.checkoutBusyCourseId) return;
    this.checkoutBusyCourseId = courseId;
    this.checkoutError = null;

    this.checkoutService.createCourseCheckoutSession(courseId).subscribe({
      next: session => this.checkoutService.redirectToHostedCheckout(session.url),
      error: err => {
        this.checkoutBusyCourseId = null;
        this.checkoutError = this.checkoutService.checkoutErrorMessage(err);
        this.cdr.markForCheck();
      }
    });
  }

  navigateToCreateCourse(orgId: string): void {
    this.router.navigate(['/instructor/courses/create'], { queryParams: { organizationId: orgId, returnUrl: '/courses' } });
  }
}
